import * as qs from 'qs';
import type { ReportCategory, ReportURLParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { reportTypeDefaultFormatMap, reportTypeFormatMap } from '../shared/constants';
import { nuc } from '../../../../../utils';
import { buildIncludeParams } from '../shared/utils';

type Query = {
  preview?: boolean;
  download?: boolean;
};

type AttributionFormat = 'preview' | 'download' | 'security' | 'public';

const getEndpoint = ({
  projectRevisionLocator,
  reportType,
  query,
  format,
  includeOptions,
  dependencyMetadataOptions,
  attributionFormat,
}: {
  projectRevisionLocator: string;
  query: Query;
  format: FormatName | null;
  reportType: ReportCategory;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  attributionFormat: AttributionFormat;
}) => {
  const defaultFileFormat = reportTypeDefaultFormatMap[reportType];
  const formatQuery = format ? reportTypeFormatMap[format] : defaultFileFormat;
  const urlParams: ReportURLParams = {
    ...buildIncludeParams(includeOptions),
    dependencyInfoOptions: dependencyMetadataOptions,
    format: formatQuery,
  };
  const queryString = qs.stringify({ ...query, ...urlParams });

  return `/api/revisions/${nuc(projectRevisionLocator)}/attribution/${attributionFormat}?${queryString}`;
};

/**
 * Get the preview URL for a report at the project level
 */
export const getPreviewURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      preview: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: reportType === 'vulnerability' ? 'security' : 'preview',
  });
};

/**
 * Get the download URL for a report at the project level
 */
export const getDownloadURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  const isVulnerability = reportType === 'vulnerability';
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      download: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: isVulnerability ? 'security' : 'download',
  });
};

/**
 * Get the link to generate a public report URL
 */
export const getGenerateReportURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {},
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: 'public',
  });
};
